import React from "react";
import { useDispatch } from "react-redux";
import MaterialModal from "../MaterialModal";
import withModal from "../withModal";
import { hideModal } from "../../../actions/actionCreators";
import { Donor } from "../../../types/stores/makeAWish";
import { TextField } from "@material-ui/core";
import { green, grey } from "@material-ui/core/colors";
import moment from "moment";

type ModalProps = {
    save: () => void;
    editData: Donor;
    donorData: Donor;
};

type Props = {
    isOpen: boolean;
    modalProps: ModalProps;
};

const PreviewModal: React.FC<Props> = ({ isOpen, modalProps }: Props) => {
    const dispatch = useDispatch();

    const { editData, donorData, save } = modalProps;

    const formatDate = (date: string) => moment(date, "MM/DD/YYYY").format("YYYY-MM-DD").toString();

    const getColor = (valueBefore, valueAfter) =>
        valueAfter != null && valueBefore !== valueAfter ? green[500] : grey[500];

    const onPrimaryClick = () => {
        save();
        hide();
    };

    const hide = () => dispatch(hideModal("PREVIEW_MAW_SAVE"));

    return editData && donorData ? (
        <MaterialModal
            id={"preview-modal"}
            isOpen={isOpen}
            onClose={hide}
            title="Preview"
            onPrimaryButtonRequest={onPrimaryClick}
            showPrimaryButton
            primaryButtonText="Save"
            cancelButtonText="Cancel"
        >
            <div>
                <p>Preview your donor data changes before saving. Changed data will display in green.</p>
                <div className="row">
                    <div className="col-md-6">
                        <span style={{ fontWeight: 500 }}>Before</span>
                    </div>
                    <div className="col-md-6">
                        <span style={{ fontWeight: 500 }}>After</span>
                    </div>
                </div>
                <div style={{ height: "400px", overflowY: "auto", overflowX: "hidden" }}>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Id"
                                label="Id"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Id, editData.Id) } }}
                                value={donorData.Id}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Id"
                                label="Id"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Id, editData.Id) } }}
                                value={editData.Id}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Title"
                                label="Title"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Title, editData.Title) } }}
                                value={donorData.Title}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Title"
                                label="Title"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Title, editData.Title) } }}
                                value={editData.Title ?? donorData.Title}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-First-Name"
                                label="First Name"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.FirstName, editData.FirstName) } }}
                                value={donorData.FirstName}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-First-Name"
                                label="First Name"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.FirstName, editData.FirstName) } }}
                                value={editData.FirstName ?? donorData.FirstName}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Middle-Name"
                                label="Middle Name"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.MiddleName, editData.MiddleName) } }}
                                value={donorData.MiddleName}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Middle-Name"
                                label="Middle Name"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.MiddleName, editData.MiddleName) } }}
                                value={editData.MiddleName ?? donorData.MiddleName}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Last-Name"
                                label="Last Name/Surname"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.LastName, editData.LastName) } }}
                                value={donorData.LastName}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Last-Name"
                                label="Last Name/Surname"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.LastName, editData.LastName) } }}
                                value={editData.LastName ?? donorData.LastName}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Last-Name-Suffix"
                                label="Surname/Last Name Suffix"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.LastNameSuffix, editData.LastNameSuffix) },
                                }}
                                value={donorData.LastNameSuffix}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Last-Name-Suffix"
                                label="Surname/Last Name Suffix"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.LastNameSuffix, editData.LastNameSuffix) },
                                }}
                                value={editData.LastNameSuffix ?? donorData.LastNameSuffix}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Birth-Date"
                                label="Birth Date"
                                type="date"
                                disabled
                                inputProps={{
                                    style: {
                                        color: getColor(
                                            donorData.BirthDate != null
                                                ? formatDate(donorData!.BirthDate)
                                                : donorData.BirthDate,
                                            editData.BirthDate
                                        ),
                                    },
                                }}
                                value={formatDate(donorData.BirthDate!)}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Birth-Date"
                                label="Birth Date"
                                type="date"
                                disabled
                                inputProps={{
                                    style: {
                                        color: getColor(
                                            donorData.BirthDate != null
                                                ? formatDate(donorData!.BirthDate)
                                                : donorData.BirthDate,
                                            editData.BirthDate
                                        ),
                                    },
                                }}
                                value={editData.BirthDate ?? formatDate(donorData!.BirthDate || "")}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Sex"
                                label="Sex"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Sex, editData.Sex) } }}
                                value={donorData.Sex}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Sex"
                                label="Sex"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Sex, editData.Sex) } }}
                                value={editData.Sex ?? donorData.Sex}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Salutation"
                                label="Salutation"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Salutation, editData.Salutation) } }}
                                value={donorData.Salutation}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Salutation"
                                label="Salutation"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Salutation, editData.Salutation) } }}
                                value={editData.Salutation ?? donorData.Salutation}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Match-Code"
                                label="Match Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.MatchCode, editData.MatchCode) } }}
                                value={donorData.MatchCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Match-Code"
                                label="Match Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.MatchCode, editData.MatchCode) } }}
                                value={editData.MatchCode ?? donorData.MatchCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Address-Type"
                                label="Address Type"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.AddressType, editData.AddressType) } }}
                                value={donorData.AddressType}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Address-Type"
                                label="Address Type"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.AddressType, editData.AddressType) } }}
                                value={editData.AddressType ?? donorData.AddressType}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-House-Number"
                                label="House Number"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.HouseNumber, editData.HouseNumber) } }}
                                value={donorData.HouseNumber}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-House-Number"
                                label="House Number"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.HouseNumber, editData.HouseNumber) } }}
                                value={editData.HouseNumber ?? donorData.HouseNumber}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Street-Name"
                                label="Street Name"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.StreetName, editData.StreetName) } }}
                                value={donorData.StreetName}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Street-Name"
                                label="Street Name"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.StreetName, editData.StreetName) } }}
                                value={editData.StreetName ?? donorData.StreetName}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Address-Line-2"
                                label="Address 2"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.AddressLine2, editData.AddressLine2) },
                                }}
                                value={donorData.AddressLine2}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Address-Line-2"
                                label="Address 2"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.AddressLine2, editData.AddressLine2) },
                                }}
                                value={editData.AddressLine2 ?? donorData.AddressLine2}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Address-Line-3"
                                label="Address 3"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.AddressLine3, editData.AddressLine3) },
                                }}
                                value={donorData.AddressLine3}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Address-Line-3"
                                label="Address 3"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.AddressLine3, editData.AddressLine3) },
                                }}
                                value={editData.AddressLine3 ?? donorData.AddressLine3}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Company"
                                label="Company"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Company, editData.Company) } }}
                                value={donorData.Company}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Company"
                                label="Company"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Company, editData.Company) } }}
                                value={editData.Company ?? donorData.Company}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-City"
                                label="City"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.City, editData.City) } }}
                                value={donorData.City}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-City"
                                label="City"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.City, editData.City) } }}
                                value={editData.City ?? donorData.City}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-County"
                                label="County"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.County, editData.County) } }}
                                value={donorData.County}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-County"
                                label="County"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.County, editData.County) } }}
                                value={editData.County ?? donorData.County}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-State"
                                label="State"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.State, editData.State) } }}
                                value={donorData.State}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-State"
                                label="State"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.State, editData.State) } }}
                                value={editData.State ?? donorData.State}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Zip"
                                label="Zip Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Zip, editData.Zip) } }}
                                value={donorData.Zip}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Zip"
                                label="Zip Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Zip, editData.Zip) } }}
                                value={editData.Zip ?? donorData.Zip}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Zip-Plus"
                                label="Zip + 4"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.ZipPlus, editData.ZipPlus) } }}
                                value={donorData.ZipPlus}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Zip-Plus"
                                label="Zip + 4"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.ZipPlus, editData.ZipPlus) } }}
                                value={editData.ZipPlus ?? donorData.ZipPlus}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Telephone"
                                label="Phone Number"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Telephone, editData.Telephone) } }}
                                value={donorData.Telephone}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Telephone"
                                label="Phone Number"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Telephone, editData.Telephone) } }}
                                value={editData.Telephone ?? donorData.Telephone}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Fax"
                                label="Fax"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Fax, editData.Fax) } }}
                                value={donorData.Fax}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Fax"
                                label="Fax"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Fax, editData.Fax) } }}
                                value={editData.Fax ?? donorData.Fax}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Email"
                                label="Email Address"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Email, editData.Email) } }}
                                value={donorData.Email}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Email"
                                label="Email Address"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.Email, editData.Email) } }}
                                value={editData.Email ?? donorData.Email}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Member-Code"
                                label="Member Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.MemberCode, editData.MemberCode) } }}
                                value={donorData.MemberCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Member-Code"
                                label="Member Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.MemberCode, editData.MemberCode) } }}
                                value={editData.MemberCode ?? donorData.MemberCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Origin-Code"
                                label="Origin Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.OriginCode, editData.OriginCode) } }}
                                value={donorData.OriginCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Origin-Code"
                                label="Origin Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.OriginCode, editData.OriginCode) } }}
                                value={editData.OriginCode ?? donorData.OriginCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Service-Center"
                                label="Service Center"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.ServiceCenter, editData.ServiceCenter) },
                                }}
                                value={donorData.ServiceCenter}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Service-Center"
                                label="Service Center"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.ServiceCenter, editData.ServiceCenter) },
                                }}
                                value={editData.ServiceCenter ?? donorData.ServiceCenter}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Residential-Business-Code"
                                label="Residential/Business Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.ResBusCode, editData.ResBusCode) } }}
                                value={donorData.ResBusCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Residential-Business-Code"
                                label="Residential/Business Code"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.ResBusCode, editData.ResBusCode) } }}
                                value={editData.ResBusCode ?? donorData.ResBusCode}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Never-Merge"
                                label="Never Merge"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.NeverMergeInd, editData.NeverMergeInd) },
                                }}
                                value={donorData.NeverMergeInd}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Never-Merge"
                                label="Never Merge"
                                type="text"
                                disabled
                                inputProps={{
                                    style: { color: getColor(donorData.NeverMergeInd, editData.NeverMergeInd) },
                                }}
                                value={editData.NeverMergeInd}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Delete-Flag"
                                label="Delete Flag"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.DeleteFlag, editData.DeleteFlag) } }}
                                value={donorData.DeleteFlag}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Delete-Flag"
                                label="Delete Flag"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.DeleteFlag, editData.DeleteFlag) } }}
                                value={editData.DeleteFlag}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Bypass-Upgrade-Logic"
                                label="Bypass Upgrade Logic"
                                type="text"
                                disabled
                                inputProps={{
                                    style: {
                                        color: getColor(donorData.BypassUpgradeLogic, editData.BypassUpgradeLogic),
                                    },
                                }}
                                value={donorData.BypassUpgradeLogic}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Bypass-Upgrade-Logic"
                                label="Bypass Upgrade Logic"
                                type="text"
                                disabled
                                inputProps={{
                                    style: {
                                        color: getColor(donorData.BypassUpgradeLogic, editData.BypassUpgradeLogic),
                                    },
                                }}
                                value={editData.BypassUpgradeLogic}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Bypass-Never-Acknowledge"
                                label="Never Acknowledge"
                                type="text"
                                disabled
                                inputProps={{
                                    style: {
                                        color: getColor(donorData.NeverAcknowledgeInd, editData.NeverAcknowledgeInd),
                                    },
                                }}
                                value={donorData.NeverAcknowledgeInd}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Never-Acknowledge"
                                label="Never Acknowledge"
                                type="text"
                                disabled
                                inputProps={{
                                    style: {
                                        color: getColor(donorData.NeverAcknowledgeInd, editData.NeverAcknowledgeInd),
                                    },
                                }}
                                value={editData.NeverAcknowledgeInd}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Donor-Archive"
                                label="Archive"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.ArchiveInd, editData.ArchiveInd) } }}
                                value={donorData.ArchiveInd}
                                style={{ width: "100%" }}
                            />
                        </div>
                        <div className="col-md-6">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="Edit-Donor-Archive"
                                label="Archive"
                                type="text"
                                disabled
                                inputProps={{ style: { color: getColor(donorData.ArchiveInd, editData.ArchiveInd) } }}
                                value={editData.ArchiveInd}
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </MaterialModal>
    ) : null;
};

export default withModal(PreviewModal, "PREVIEW_MAW_SAVE");
